import React, { useState, useEffect, useCallback } from "react";
import { compose } from "recompose";

import { withFirebase } from "../Firebase";
import { withAuthorization } from "../Session";
import * as ROLES from "../../constants/roles";

import "./index.css";
import EnrollmentReport from "./components/EnrollmentReport";
import ParticipantLookup from "./components/ParticipantLookup";
import AdminDropoutAnalysis from "./components/DropoutAnalysis";
import MentorTracking from "./components/MentorTracking";
import ResourceMatching from "./components/ResourceMatching";
import ReportSettings from "./components/PreviewReportsModal";
import ResumeFeedback from "./components/ResumeFeedback";
import FunctionHealth from "./components/FunctionHealth";

const CACHE_KEY = "admin__cachedData";
const CACHE_TTL = 30 * 60 * 1000; // 30 minutes

const USER_FIELDS = [
  "email", "displayName", "username", "isMentor", "isAdmin",
  "classId", "classUID", "inactive", "createdAt", "latestWebLogin", "latestPhoneLogin", "lastLogin",
  "phone", "phoneNumber", "lessons", "notificationPreferences",
];

const CLASSROOM_FIELDS = [
  "className", "classroomClosed", "teachers", "teacherId",
  "settings", "countyName", "scholarships", "usedScholarships", "programSurvey",
];

const stripUser = (user) => {
  const slim = {};
  for (const key of USER_FIELDS) {
    if (user[key] !== undefined) slim[key] = user[key];
  }
  return slim;
};

const stripClassroom = (classroom) => {
  const slim = {};
  for (const key of CLASSROOM_FIELDS) {
    if (classroom[key] !== undefined) slim[key] = classroom[key];
  }
  return slim;
};

const getCached = () => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.timestamp > CACHE_TTL) return null;
    return parsed;
  } catch {
    return null;
  }
};

const setCache = (users, classrooms) => {
  const entry = { users, classrooms, timestamp: Date.now() };
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    // sessionStorage full — silently skip
  }
  return entry.timestamp;
};

const Admin = ({ firebase }) => {
  const [activeTab, setActiveTab] = useState("enrollment");
  const [cachedUsers, setCachedUsersState] = useState(null);
  const [cachedClassrooms, setCachedClassroomsState] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const fetchData = useCallback(
    async (force = false) => {
      if (!force) {
        const cached = getCached();
        if (cached) {
          setCachedUsersState(cached.users);
          setCachedClassroomsState(cached.classrooms);
          setLastUpdated(cached.timestamp);
          return;
        }
      }
      setLoadingData(true);
      try {
        const [usersSnap, classroomsSnap] = await Promise.all([
          firebase.users().once("value"),
          firebase.classrooms().once("value"),
        ]);
        const usersObj = usersSnap.val() || {};
        const classroomsObj = classroomsSnap.val() || {};

        const slimUsers = {};
        for (const [uid, user] of Object.entries(usersObj)) {
          slimUsers[uid] = stripUser(user);
        }
        const slimClassrooms = {};
        for (const [id, classroom] of Object.entries(classroomsObj)) {
          slimClassrooms[id] = stripClassroom(classroom);
        }

        const ts = setCache(slimUsers, slimClassrooms);
        setCachedUsersState(slimUsers);
        setCachedClassroomsState(slimClassrooms);
        setLastUpdated(ts);
      } catch (err) {
        console.error("Error fetching admin data:", err);
      }
      setLoadingData(false);
    },
    [firebase]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, [fetchData]);

  const formatLastUpdated = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  };

  return (
    <div className="admin-container">
      <div className="admin__header">
        <h1>Admin</h1>
        <div className="admin__last-updated">
          {lastUpdated && (
            <span className="admin__last-updated__text">
              Last updated: {formatLastUpdated(lastUpdated)}
            </span>
          )}
          <button
            className="admin__refresh-btn"
            onClick={() => fetchData(true)}
            disabled={loadingData}
          >
            {loadingData ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>
      <div className="admin__tabs">
        <button
          className={`admin__tab ${activeTab === "enrollment" ? "admin__tab--active" : ""}`}
          onClick={() => setActiveTab("enrollment")}
        >
          Enrollment Report
        </button>
        <button
          className={`admin__tab ${activeTab === "participants" ? "admin__tab--active" : ""}`}
          onClick={() => setActiveTab("participants")}
        >
          Users
        </button>
        <button
          className={`admin__tab ${activeTab === "dropout" ? "admin__tab--active" : ""}`}
          onClick={() => setActiveTab("dropout")}
        >
          Dropout Analysis
        </button>
        <button
          className={`admin__tab ${activeTab === "mentor-tracking" ? "admin__tab--active" : ""}`}
          onClick={() => setActiveTab("mentor-tracking")}
        >
          Coordinator Tracking
        </button>
        <button
          className={`admin__tab ${activeTab === "resource-matching" ? "admin__tab--active" : ""}`}
          onClick={() => setActiveTab("resource-matching")}
        >
          Resource Matching
        </button>
        <button
          className={`admin__tab ${activeTab === "reports" ? "admin__tab--active" : ""}`}
          onClick={() => setActiveTab("reports")}
        >
          Reports
        </button>
        <button
          className={`admin__tab ${activeTab === "resume-feedback" ? "admin__tab--active" : ""}`}
          onClick={() => setActiveTab("resume-feedback")}
        >
          Resume Feedback
        </button>
        <button
          className={`admin__tab ${activeTab === "function-health" ? "admin__tab--active" : ""}`}
          onClick={() => setActiveTab("function-health")}
        >
          Function Health
        </button>
      </div>
      {activeTab === "enrollment" && (
        <EnrollmentReport cachedUsers={cachedUsers} cachedClassrooms={cachedClassrooms} />
      )}
      {activeTab === "participants" && (
        <ParticipantLookup cachedUsers={cachedUsers} cachedClassrooms={cachedClassrooms} />
      )}
      {activeTab === "dropout" && (
        <AdminDropoutAnalysis cachedUsers={cachedUsers} cachedClassrooms={cachedClassrooms} />
      )}
      {activeTab === "mentor-tracking" && (
        <MentorTracking cachedUsers={cachedUsers} cachedClassrooms={cachedClassrooms} />
      )}
      {activeTab === "resource-matching" && (
        <ResourceMatching cachedClassrooms={cachedClassrooms} />
      )}
      {activeTab === "reports" && (
        <ReportSettings
          classrooms={cachedClassrooms}
          users={cachedUsers}
          firebase={firebase}
        />
      )}
      {activeTab === "resume-feedback" && <ResumeFeedback />}
      {activeTab === "function-health" && <FunctionHealth />}
    </div>
  );
};

const condition = (authUser) => authUser && !!authUser.roles[ROLES.ADMIN];

export default compose(withAuthorization(condition), withFirebase)(Admin);
